import { formatMs, type ProviderDefinitionDto, type ProviderView } from "../../lib/api";
import {
  capChips,
  credentialsLabel,
  freeBadge,
  isNoKey,
  quotaLabel,
  requirementLabel,
} from "../../lib/provider-display";
import { ProviderMark, StatusDot } from "../../components/ui";

/* ── Connected provider — compact horizontal row ───────────────── */

export function ConnectedRow({
  provider: p,
  onOpen,
  demo,
}: {
  provider: ProviderView;
  onOpen: () => void;
  demo?: boolean;
}) {
  const stats = [
    `${p.modelCount} model${p.modelCount === 1 ? "" : "s"}`,
    `${p.freeModelCount} free`,
    p.health?.p50LatencyMs != null ? `~${formatMs(p.health.p50LatencyMs)}` : null,
    credentialsLabel(p),
  ].filter(Boolean);

  return (
    <button
      onClick={onOpen}
      className={`panel group flex w-full items-center gap-4 px-4 py-3 text-left transition hover:border-accent/40 ${
        demo ? "border-dashed border-canvas-border opacity-90" : ""
      }`}
    >
      <ProviderMark letter={p.definition.branding.letter} accent={p.definition.branding.accent} size={34} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="truncate text-sm font-semibold">{p.name}</h3>
          {demo && <span className="chip-muted !text-[10px]">DEMO</span>}
          {p.connectionMode === "anonymous" && !demo && (
            <span className="chip-ok !text-[10px]">NO KEY</span>
          )}
        </div>
        <p className="mt-0.5 truncate text-xs text-canvas-muted">
          {stats.join("  ·  ")}
        </p>
      </div>
      <div className="hidden shrink-0 items-center gap-3 sm:flex">
        {p.quota && (
          <span className="text-xs text-canvas-muted" title="Quota">
            {quotaLabel(p)}
          </span>
        )}
        <StatusDot status={p.connectionStatus} />
      </div>
      <div className="sm:hidden">
        <StatusDot status={p.connectionStatus} showLabel={false} />
      </div>
    </button>
  );
}

/* ── Catalog tile — compact add-provider card ──────────────────── */

export function CatalogTile({
  def,
  onConnect,
}: {
  def: ProviderDefinitionDto;
  onConnect: () => void;
}) {
  const badge = freeBadge(def);
  const noKey = isNoKey(def);
  const badgeTone =
    badge == null
      ? "chip-muted"
      : badge === "PAID" || badge === "EVAL ONLY" || badge === "TIME-LIMITED"
        ? "chip-muted"
        : badge === "FREE TRIAL"
          ? "chip-warn"
          : "chip-ok";

  return (
    <article className="panel group flex flex-col p-4 transition hover:border-accent/40">
      <div className="mb-2.5 flex items-start gap-3">
        <ProviderMark letter={def.branding.letter} accent={def.branding.accent} size={34} />
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold">{def.name}</h3>
          <p className="truncate text-xs text-canvas-muted">{def.shortDescription}</p>
        </div>
      </div>
      <div className="mb-2 flex flex-wrap gap-1">
        {badge && <span className={`${badgeTone} !text-[10px]`}>{badge}</span>}
        {noKey && <span className="chip-ok !text-[10px]">NO KEY</span>}
      </div>
      <p className="mb-3 text-[11px] text-canvas-muted">
        {capChips(def.capabilities, 4).join("  ·  ")}
      </p>
      <div className="mt-auto">
        <p className="mb-2 text-[11px] text-canvas-muted">{requirementLabel(def)}</p>
        <button
          className={`w-full justify-center ${noKey ? "btn-ghost border-accent/30 text-accent hover:border-accent/60" : "btn-ghost"}`}
          onClick={onConnect}
        >
          {noKey ? "Connect without key" : "Connect"}
        </button>
      </div>
    </article>
  );
}
